<template>
  <div>
    <Feature :feature-slug="featureNames.GOOGLE_REVIEW">
      <v-divider class="mb-6" />
      <div class="mb-6">
        <GoogleReviewLinkInput
          :company-id="wizardCompany?.id"
          :model-value="formData.googleReviewLink"
          :show-success-snackbar="false"
          :text="{
            label: $t('onboarding.googleReview.label'),
            description: $t('onboarding.googleReview.description'),
            learnLinkLabel: $t('onboarding.googleReview.learnLinkLabel'),
            helpUrl: $t('onboarding.googleReview.helpUrl'),
            placeholder: $t('onboarding.googleReview.placeholder'),
            error: {
              general: $t('onboarding.googleReview.error.general'),
              invalid: $t('onboarding.googleReview.error.invalid'),
            },
          }"
          @update:model-value="updateField('googleReviewLink', $event || '')"
        />
      </div>
    </Feature>

    <Feature :feature-slug="featureNames.MAKLER_MITTEL">
      <v-divider class="my-6" />
      <ChatWidgetIntegration
        form-key="automaticIntegration"
        :initial-value="widgetInitialValue"
        @confirm="onWidgetIntegrationConfirm"
      />
    </Feature>

    <v-divider class="my-6" />
    <OnboardingLegalConsents
      id="onboarding-legal-consents"
      v-model="legalConsentsModel"
      :company="wizardCompany"
      :is-us-onboarding="isUsOnboarding"
      :show-errors="validation?.missingFieldsRevealed.value ?? false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import ChatWidgetIntegration from '@/components/ai/onboarding/ChatWidgetIntegration.vue'
import OnboardingLegalConsents, {
  type LegalConsentsValues,
} from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import GoogleReviewLinkInput from '@/components/GoogleReviewLinkInput.vue'
import featureNames from '@/lib/featureNames'
import { Feature } from '@/plugins/feature-config'

import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import { useOnboardingValidationContext } from '../../composables/onboardingValidationContext'
import type { OnboardingWizardCompany } from '../../lib/onboardingWizardCompany'

const props = defineProps<{
  wizardCompany?: OnboardingWizardCompany | null
  legalConsents?: LegalConsentsValues | null
}>()

const emit = defineEmits<{
  'update:legalConsents': [value: LegalConsentsValues]
  'widget-integration': [payload: { enabled: boolean }]
}>()

const { formData, aiCapturedFields, updateField, isUsOnboarding } = useOnboardingDataEntryContext()
const validation = useOnboardingValidationContext()

const EMPTY_LEGAL_CONSENTS: LegalConsentsValues = {
  acceptAgb: false,
  acceptPrivacy: false,
  acceptGuideline: false,
  authorizeSendOnBehalf: false,
  contactViaEmail: false,
  contactViaWhatsapp: false,
  acceptConfirmData: false,
}

const legalConsentsModel = computed<LegalConsentsValues>({
  get: () => props.legalConsents ?? EMPTY_LEGAL_CONSENTS,
  set: (value) => emit('update:legalConsents', value),
})

const widgetInitialValue = computed(() =>
  formData.value.automaticIntegration === true
    ? true
    : formData.value.automaticIntegration === false &&
        aiCapturedFields.value.has('automaticIntegration')
      ? false
      : null
)

const onWidgetIntegrationConfirm = ({ enabled }: { enabled: boolean }) => {
  updateField('automaticIntegration', enabled)
  emit('widget-integration', { enabled })
}
</script>
